import { FeatureModule } from '@store/features/types';
import appointmentsReducer, {
  DEFAULT_CALENDAR_END_HOUR,
  DEFAULT_CALENDAR_START_HOUR,
} from './slice';
import { appointmentsApiSlice } from './api';
import storage from 'redux-persist/lib/storage'; // localStorage

/**
 * Appointments feature module definition
 * Configures persistence for user preferences and integrates with RTK Query
 */
export const appointmentsFeature: FeatureModule = {
  // Basic feature identification
  id: 'appointments',
  name: 'Appointments',

  // Optional feature dependencies
  dependencies: [], // No dependencies for this feature

  // Feature reducers
  reducers: {
    appointments: appointmentsReducer,
  },

  /**
   * NO MIDDLEWARE. `appointmentsApiSlice` is not an API of its own: it is
   * `baseApi` with endpoints injected, and RTK Query's `injectEndpoints`
   * returns the SAME api object it was called on. So
   * `appointmentsApiSlice.middleware === baseApi.middleware`, which the store
   * already registers statically (`store/index.ts`).
   *
   * Listing it here handed that identical middleware to `dynamicMiddleware`,
   * which sits in the same chain — so every action reached RTK Query's
   * middleware TWICE, and every `invalidatesTags` fired twice per mutation
   * fulfillment. One archive refetched the claim-work queue twice; one
   * add-note wrote two provisional notes. See `server-ui/store/hooks.ts`,
   * where the same symptom was worked around by hand.
   *
   * Only an api created with its own `createApi` (the streaming apis in
   * telehealth, note-ai-edit, rule-build, admin-v2, provider-agent-chat)
   * belongs in a feature's middleware list.
   */
  middleware: [],

  // Configure persistence for user preferences and view state
  persistence: {
    enabled: true,
    key: 'appointments',
    storage: storage, // Use localStorage for persistence across browser restarts

    // Only persist user preferences, not transient data
    whitelist: [
      'viewMode', // Calendar view mode (day/week/month/list)
      'displayMode', // Display preference (calendar/list)
      'currentDate', // Currently selected date
      'calendarGranularity', // Calendar time slot and step settings
      'weekStartDay', // First day of week shown in week view
      'calendarStartHour', // Visible-hours scroll bound (start)
      'calendarEndHour', // Visible-hours scroll bound (end)
      'clusterBusyHours', // Week-view capacity clustering preference
      'groupByMode', // Day-view label-lens mode (off / group / label)
      'groupByLabelGroupId', // Lens target id (group or single label)
      'splitLanesByProvider', // Expand lanes into provider sub-columns
      'orgScheduleFilters', // Org schedule filter selections (provider/patient)
    ],

    // Don't persist transient or computed state
    blacklist: [
      'appointments', // Appointment data (loaded from API)
      'nextAppointment', // Next appointment (loaded from API)
      'isTutorialMode', // Tutorial state (should not persist)
      'mockAppointments', // Mock data
    ],

    version: 2,
    migrations: {
      1: state => {
        if (
          state?.calendarStartHour === DEFAULT_CALENDAR_START_HOUR &&
          state?.calendarEndHour === 20
        ) {
          return {
            ...state,
            calendarEndHour: DEFAULT_CALENDAR_END_HOUR,
          };
        }

        return state;
      },
    },
    mergeStrategy: 'merge',
  },

  // Configure cross-tab synchronization for real-time updates
  sync: {
    enabled: false,
  },

  // Feature lifecycle hooks
  setup: store => {
    console.log(`${appointmentsFeature.name} feature initialized`);

    // Initialize default state from localStorage if available
    const state = store.getState();
    if (state.appointments) {
      // Feature is loaded and ready
      console.log('Appointments feature state loaded from persistence');
    }
  },

  cleanup: store => {
    console.log(`${appointmentsFeature.name} feature cleaned up`);

    // Cleanup any subscriptions or timers if needed
    // The RTK Query cache will be cleaned up automatically
  },
};

/**
 * Initialize feature - call this when registering the feature
 */
export function initializeAppointmentsFeature() {
  return {
    api: appointmentsApiSlice,
    feature: appointmentsFeature,
  };
}
